import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { fmtDate } from '../utils/formatUtils';
import { t } from '../i18n/nl.js';

const FILTERS = ['all', 'training', 'notes', 'membership', 'finance'];

function timestamp(value) {
  const parsed = value ? new Date(value).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

export default function ClientRecentActivityPanel({ clientId, credits, invoices, membershipHistory, appointments, initialLimit = 5 }) {
  const [notes, setNotes] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      const [notesRes, workoutsRes] = await Promise.all([
        supabase.from('client_notes')
          .select('id, body, created_at, author:profiles!client_notes_author_id_fkey(full_name)')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false })
          .limit(8),
        supabase.from('workout_sessions')
          .select('id, performed_on, completed_at, template:client_exercise_templates(name)')
          .eq('client_id', clientId)
          .not('completed_at', 'is', null)
          .order('performed_on', { ascending: false })
          .limit(8),
      ]);
      if (!active) return;
      setNotes(notesRes.data || []);
      setWorkouts(workoutsRes.data || []);
    }
    load();
    return () => { active = false; };
  }, [clientId]);

  const activities = useMemo(() => {
    const items = [
      ...notes.map(note => ({
        id: `note-${note.id}`,
        type: 'notes',
        at: note.created_at,
        title: t('clientActivity.noteAdded'),
        detail: note.body,
        meta: note.author?.full_name || t('clientNotes.unknownAuthor'),
      })),
      ...workouts.map(workout => ({
        id: `workout-${workout.id}`,
        type: 'training',
        at: workout.completed_at || workout.performed_on,
        title: t('clientActivity.workoutCompleted'),
        detail: workout.template?.name || t('clientActivity.workoutWithoutTemplate'),
      })),
      ...(appointments || []).filter(appointment => timestamp(appointment.starts_at) <= Date.now()).slice(0, 8).map(appointment => ({
        id: `appointment-${appointment.id}`,
        type: 'training',
        at: appointment.starts_at,
        title: t('clientActivity.appointment'),
        detail: appointment.name || t('clientActivity.training'),
      })),
      ...(credits || []).slice(0, 8).map(credit => ({
        id: `credit-${credit.id}`,
        type: 'membership',
        at: credit.created_at,
        title: credit.delta > 0 ? t('clientActivity.creditsAdded') : t('clientActivity.creditsUsed'),
        detail: `${credit.delta > 0 ? '+' : ''}${credit.delta} ${t('clientActivity.credits')}`,
      })),
      ...(membershipHistory || []).slice(0, 8).map(item => ({
        id: `membership-${item.id}`,
        type: 'membership',
        at: item.created_at,
        title: t('clientActivity.membershipChanged'),
        detail: item.membership_type || '—',
      })),
      ...(invoices || []).slice(0, 8).map(invoice => ({
        id: `invoice-${invoice.id}`,
        type: 'finance',
        at: invoice.created_at,
        title: t('clientActivity.invoiceCreated'),
        detail: invoice.invoice_number || '—',
      })),
    ];
    return items.sort((a, b) => timestamp(b.at) - timestamp(a.at)).slice(0, 12);
  }, [appointments, credits, invoices, membershipHistory, notes, workouts]);

  const filtered = filter === 'all' ? activities : activities.filter(item => item.type === filter);
  const visible = expanded ? filtered : filtered.slice(0, initialLimit);

  return (
    <section className="card client-activity-panel">
      <div className="client-detail-card-head">
        <h2>{t('clientActivity.title')}</h2>
        <span>{t('clientActivity.latest')}</span>
      </div>
      <div className="client-activity-filters">
        {FILTERS.map(value => (
          <button key={value} className={filter === value ? 'is-active' : ''} onClick={() => { setFilter(value); setExpanded(false); }}>
            {t(`clientActivity.filters.${value}`)}
          </button>
        ))}
      </div>
      <div className="client-activity-list">
        {visible.length === 0 ? <p className="text-muted">{t('clientActivity.empty')}</p> : visible.map(item => (
          <article key={item.id} className={`client-activity-item is-${item.type}`}>
            <span className="client-activity-dot" />
            <div><strong>{item.title}</strong><p>{item.detail}</p>{item.meta && <small>{item.meta}</small>}</div>
            <time>{fmtDate(item.at)}</time>
          </article>
        ))}
      </div>
      {filtered.length > initialLimit && (
        <button className="client-activity-toggle" onClick={() => setExpanded(value => !value)}>
          {expanded ? t('clientActivity.showLess') : t('clientActivity.showMore', { count: filtered.length - initialLimit })}
        </button>
      )}
    </section>
  );
}
